import $ from 'jquery';
import mdModal from 'ui/mdmodal';
import Callbacks from 'lib/callbacks.js';
import 'theme/moydom/less/blocks/modal-img.less';
import defaultSliderTemplate from 'template/ui/image-slider-content.twig';
/**
 * Объект управления настройками слайдера
 */
class Config {
    constructor(opts = {}) {
        // Уничтожать ли элементы изображений при скрывании слайдера
        this.static = opts.static;
    }
}

/**
 * Слайд в очереди
 */
class Slide {
    static getNextUID = (() => {
        var UID = 1;

        return () => UID++;
    })();

    constructor(src, opts = {}, events = {}) {
        if (!src) {
            throw new Error('No image src for slide');
        }

        this.UID = Slide.getNextUID();
        this.src = src;
        this.opts = opts;
        this.events = new Callbacks(events);

        this.image = null;
        this.isActive = false;
    }

    get loaded() {
        return this.image && this.image.get(0).complete;
    }

    /**
     * Загрузить изображение для слайда
     *
     * @return {Promise} promise
     */
    load() {
        return new Promise((resolve, reject) => {
            if (!this.src) {
                reject('No image for load');
                return;
            }

            if (this.image) {
                if (this.loaded) {
                    resolve(this.image);
                    return;
                }
                this.image
                    .on('load', () => {
                        resolve(this.image);
                    })
                    .on('error', () => {
                        reject();
                    });
                return;
            }

            this.image = $('<img class="img-modal-img" />')
                .on('load', () => {
                    resolve(this.image);
                })
                .on('error', () => {
                    reject();
                })
                .on('click', ::this.onImageClicked)
                .attr('src', this.src);
        });
    }

    onImageClicked() {
        if (!this.loaded) {
            return;
        }

        this.events.trigger('click');
    }

    detach() {
        if (!this.image) {
            return;
        }

        this.image.detach();
    }

    destroy() {
        if (!this.image) {
            return;
        }

        this.image.detach().remove();
        this.image = null;
    }
}

/**
 * Очередь слайдов
 */
class SlidesQueue {
    constructor() {
        this.slides = [];
        this.activeIdx = null;
    }

    /**
     * Текущая длинна очереди
     *
     * @return {Number}
     */
    get length() {
        return this.slides.length;
    }

    get maxIndex() {
        return this.length - 1;
    }

    /**
     * Всего одна картинка в слайдере
     *
     * @return {Boolean} isSingle
     */
    get isSingleSlide() {
        return this.slides.length < 2;
    }

    add(image, opts, events) {
        if (!image) {
            console.warn('Try to create empty image slide');
            return;
        }

        const slide = new Slide(image, opts, events);

        this.slides.push(slide);
    }

    next() {
        if (!this.slides.length) {
            return null;
        }

        if (this.activeIdx === null || this.activeIdx < 0) {
            this.activeIdx = -1;
        }

        this.activeIdx++;

        if (this.activeIdx > this.maxIndex) {
            this.activeIdx = 0;
        }

        try {
            return this.slides[this.activeIdx];
        } catch (err) {
            console.error(err);
            return null;
        }
    }

    prev() {
        if (!this.slides.length) {
            return null;
        }

        if (this.activeIdx === null || this.activeIdx > this.maxIndex) {
            this.activeIdx = this.maxIndex + 1;
        }

        this.activeIdx--;

        if (this.activeIdx < 0) {
            this.activeIdx = this.maxIndex;
        }

        try {
            return this.slides[this.activeIdx];
        } catch (err) {
            console.error(err);
            return null;
        }
    }

    /**
     * Сбросить информацию о текущем слайде
     *
     * @return {SlidesQueue} this
     */
    dropActiveIdx() {
        this.activeIdx = null;
        return this;
    }

    /**
     * Убрать со страницы все изображения
     */
    detach() {
        this.slides.forEach((slide) => {
            slide.detach();
        });
    }

    /**
     * Разрушить объекты изображений
     */
    destroy() {
        this.slides.forEach((slide) => {
            slide.destroy();
        });
    }

    /**
     * Удалить все изображения
     */
    clear() {
        this.destroy();
        this.slides = [];
    }
}

/**
 * Слайдер изображений
 */
export default class ImageSlider {
    constructor(opts = {}) {
        this.inited = false;
        this.modal = null;
        this.slide = null;

        this.queue = new SlidesQueue();
        this.config = new Config(opts);
    }

    /**
     * Добавить изображение к слайдеру
     *
     * @param {string} image src изображения
     */
    add(image) {
        this.queue.add(image, {}, {});
        return this;
    }

    /**
     * Показать слайдер
     */
    show() {
        if (!this.modal) {
            this.modal = mdModal.add(
                {
                    classes: {
                        layout: 'modal img-modal fade',
                        content: 'img-modal-table',
                    },
                    width: '',
                    header: false,
                    structure: {
                        background: {
                            content: true,
                            loader: true,
                        },
                    },
                    templates: {
                        content: `<table
                            class="{{ classes }}"
                            {% if styles not empty %}
                                style="{{ styles }}
                            {% endif %}"
                        ></table>`,
                    },
                    content: this._getSliderContent(),
                },
                {
                    show: () => {
                        this.init();
                        this.next();
                    },
                    hide: () => {
                        this.slide = null;
                        this.queue.dropActiveIdx().detach();
                    },
                    destroy: () => {
                        this.modal = null;
                        this.inited = false;

                        if (!this.config.static) {
                            this.queue.destroy();
                        }
                    },
                }
            );
        }

        this.modal.show();

        return this;
    }

    setIndex(index = 0) {
        this.queue.activeIdx = index - 1;

        if (this.shown) {
            this.next();
        }

        return this;
    }

    /**
     * Закрыть модальное окно
     */
    close() {
        if (!this.modal) {
            return;
        }
        this.modal.close();
    }

    /**
     * Очистить информацию об изображениях
     */
    clear() {
        this.queue.clear();
        this.refreshCurrentSlide();
    }

    /**
     * Модальное окно отображается
     * @return {boolean} shown
     */
    get shown() {
        return this.modal && this.modal.shown();
    }

    /**
     * Инициализация структуры после создания модального окна
     */
    init() {
        if (this.inited || !this.modal) {
            return;
        }

        this.container = $(this.modal.container());
        this.content = $(this.modal.content());

        this.imageHolder = this.content.find('.js-image');
        this.nextButton = this.content.find('.js-next');
        this.prevButton = this.content.find('.js-prev');
        this.currentNumberElement = this.content.find('.js-number-current');
        this.totalNumberElement = this.content.find('.js-number-total');

        this.nextButton.on('click', ::this.next);
        this.prevButton.on('click', ::this.prev);
        this.content.on('click', '.js-close', ::this.close);

        this.interfaceDisplayed = true;
        this.inited = true;
    }

    /**
     * Обновить отображение текущего слайда
     */
    refreshCurrentSlide() {
        if (!this.shown) {
            return;
        }

        if (!this.slide) {
            return;
        }

        this.displyLoading(true);
        this.slide.load().then(
            (image) => {
                this.imageHolder.html(image);
                // this.container.width(image.width());
                this.displyLoading(false);
            },
            (err) => {
                console.error(err);
            }
        );
    }

    /**
     * Отобразить следующий слайд
     */
    next() {
        if (!this.shown) {
            return;
        }

        // hide interface for alone slide
        this.displayInterface(!this.queue.isSingleSlide);
        this.displaySlide(this.queue.next());
    }

    /**
     * Отобразить предыдущий слайд
     */
    prev() {
        if (!this.shown) {
            return;
        }

        // hide interface for alone slide
        this.displayInterface(!this.queue.isSingleSlide);
        this.displaySlide(this.queue.prev());
    }

    displaySlide(slide) {
        if (!slide) {
            console.warn('Slide not found');
            return;
        }

        if (this.slide && this.slide.UID === slide.UID) {
            return;
        }

        this.slide = slide;
        this.currentNumber = this.queue.activeIdx + 1;
        this.totalNumber = this.queue.length;
        this.refreshCurrentSlide();
    }

    /**
     * Отобразить текущий номер слайда
     *
     * @param  {Number} value
     */
    set currentNumber(value) {
        this.currentNumberElement.html(value);
    }

    /**
     * Отобразить общее число слайдов
     *
     * @param  {Number} value
     */
    set totalNumber(value) {
        this.totalNumberElement.html(value);
    }

    /**
     * Возвращает контент модального окна слайдера
     *
     * @return {object}
     */
    _getSliderContent() {
        return defaultSliderTemplate();
    }

    displayInterface(value) {
        if (value) {
            if (this.interfaceDisplayed) {
                return;
            }

            this.nextButton.show();
            this.prevButton.show();
            this.interfaceDisplayed = true;
        } else {
            if (!this.interfaceDisplayed) {
                return;
            }

            this.nextButton.hide();
            this.prevButton.hide();
            this.interfaceDisplayed = false;
        }
    }

    displyLoading(value) {
        if (value) {
            this.imageHolder.addClass('loading');
        } else {
            this.imageHolder.removeClass('loading');
        }
    }
}
